import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useRouter } from 'next/router'

import { useLoaderStore } from '@/store/loader'
import { pageHasHero } from '@/utils/page'

import { MaskTransition, MaskTransitionHandler } from './components'
import { cn } from '@/lib/utils'
import { WPPageData } from '@/types/wp'

export const TransitionContext = createContext<undefined>(undefined)

const TransitionProvider = ({
  pageProps,
  children
}: PropsWithChildren<{ pageProps: WPPageData }>) => {
  const router = useRouter()
  const active = useRef(false)
  const nodeRef = useRef<HTMLElement>(null!)
  const maskRef = useRef<MaskTransitionHandler>(null!)
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)
  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])
  // const setTransitionState = useTransitionStore((state) => state.setState)

  const setPageVisibility = (visible: boolean) => {
    if (!nodeRef.current) return
    gsap.set(nodeRef.current, {
      autoAlpha: visible ? 1 : 0
    })
  }

  const handleLeaveTransition = () => {
    active.current = false
    setLoaderState({ heroLoaded: false })
    setPageVisibility(true)
    // setTransitionState({ active: true })
    document.documentElement.classList.add('page-transition')
  }

  const handleEnterTransition = () => {
    active.current = true
    setPageVisibility(false)
  }

  const handleEndListener = async (done: () => void) => {
    if (!maskRef.current) {
      done()
      return
    }

    if (!active.current) {
      await maskRef.current.enterTimeline()
      ScrollTrigger.getAll().forEach((t) => t.kill())
      window.scrollTo({ top: 0, left: 0 })
      done()
      return
    }

    if (!isLoaded()) {
      await loaderStateWatcher()
    }

    setPageVisibility(true)

    // setTransitionState({ reveal: true })
    await maskRef.current.leaveTimeline()
    // setTransitionState({ active: false, reveal: false })
    document.documentElement.classList.remove('page-transition')
    done()
  }

  const isLoaded = useCallback(
    () =>
      useLoaderStore.getState().heroLoaded &&
      document.fonts.status === 'loaded',
    []
  )

  const loaderStateWatcher = () =>
    new Promise<void>((resolve) => {
      const tick = () => {
        if (isLoaded()) {
          gsap.ticker.remove(tick)
          resolve()
        }
      }
      gsap.ticker.add(tick)
    })

  const contextValue = useMemo(() => undefined, [])

  useEffect(() => {
    ScrollTrigger.clearScrollMemory('manual')

    router.beforePopState((state) => {
      state.options.scroll = false
      return true
    })
  }, [router])

  return (
    <TransitionContext.Provider value={contextValue}>
      <SwitchTransition mode="out-in">
        <Transition
          key={router.asPath}
          nodeRef={nodeRef}
          addEndListener={handleEndListener}
          onEnter={handleEnterTransition}
          onExit={handleLeaveTransition}
          unmountOnExit
        >
          <main
            ref={nodeRef}
            className={cn('static', {
              'pt-[var(--header-height)]': !hasHero
            })}
          >
            {children}
          </main>
        </Transition>
      </SwitchTransition>
      <MaskTransition ref={maskRef} />
    </TransitionContext.Provider>
  )
}

export default TransitionProvider
